import React, { useEffect, useRef, useState } from "react";
import Heading from "@/components/atoms/Heading";
import BankAccountTable from "@/components/organisms/SettingsTables/BankAccountTable";
import { SettingsFormContent, SettingsFormCTAs, SettingsHeader } from "@/components/pages/Settings/index";
import { Grid } from "@mantine/core";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import { useAuth } from "@/contexts/AuthContext";
import Space from "@/components/atoms/Space";
import { Company } from "@/types";
import { useGetCompanyById } from "@/services/Company";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import {
  putUpdateCompanyIndexRegulationPercentage,
  updateCompany,
  useGetCompanyIndexRegulationPercentage,
} from "@/services/Settings";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  InvoicingFormInput,
  InvoicingValidationSchema,
} from "@/components/pages/Settings/validation/invoicingValidationSchema";
import {
  IndexRegulationFormInput,
  IndexRegulationValidationSchema,
} from "@/components/pages/Settings/validation/indexRegulationValidationSchema";
import Stack from "@/components/atoms/Stack";
import TextInput from "@/components/atoms/TextInput";
import NumberInput from "@/components/atoms/NumberInput";
import { notifications } from "@mantine/notifications";
import Checkbox from "@/components/atoms/Checkbox";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

export const EconomySettings: React.FC = () => {
  const [company, setCompany] = useState<Company>({} as Company);

  const { currentCompany, isAdminUser } = useAuth();
  const { t } = useTranslation();
  const isAdmin = isAdminUser(currentCompany);

  const { data: companyData, isLoading: isLoadingCompanyData } = useGetCompanyById(
    currentCompany?.companyId as number,
    {
      enabled: !!currentCompany?.companyId,
    }
  );

  useEffect(() => {
    if (companyData && !isLoadingCompanyData) {
      setCompany(companyData);
    }
  }, [companyData, isLoadingCompanyData]);

  return (
    <Stack data-testid="economy-settings">
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.economy")}
      </Heading>
      <InvoicingForm company={company} isAdmin={isAdmin} />
      <IndexRegulationForm companyId={currentCompany?.companyId as number} isAdmin={isAdmin} />
      <BankAccountTable
        bankAccounts={company.bankAccount || []}
        isAdmin={isAdmin}
        currentCompanyId={currentCompany?.companyId}
      />
    </Stack>
  );
};

export default EconomySettings;
interface InvoicingFormProps {
  company?: Company;
  isAdmin: boolean;
}

const InvoicingForm = (props: InvoicingFormProps) => {
  const { company, isAdmin } = props;
  const queryClient = useQueryClient();
  const ref = useRef<HTMLFormElement>(null);

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<InvoicingFormInput>({
    resolver: zodResolver(InvoicingValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: {
      invoiceemail: company?.invoiceemail || "",
      alwaysInvoice: company?.alwaysInvoice || false,
    },
  });

  const { mutate: updateInvoicingDetails, isPending } = useMutation({
    mutationFn: async (details: Company) => {
      return await updateCompany(details);
    },
    onSuccess: (_) => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.updateSuccess"),
          t("notifications:settings.updateInvoicing"),
          "invoicing-updated-success"
        )
      );
      queryClient.invalidateQueries({
        queryKey: ["getCompanyById", company?.id],
      });
    },
    onError: (_) => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.updateError"),
          t("notifications:settings.updateInvoicingError"),
          "invoicing-updated-error"
        )
      );
    },
    onMutate: async (details: Company) => {
      const queryKey = ["getCompanyById", company?.id];
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData<Company>(queryKey);
      queryClient.setQueryData<Company>(queryKey, (old) => (old ? { ...old, ...details } : old));
      return { snapshot };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["getCompanyById", company?.id] });
    },
  });

  const onSubmit = async (data: InvoicingFormInput) => {
    updateInvoicingDetails({ ...company, ...data } as Company);
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)} data-testid="invoicing-form">
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:economySettings.invoicing")}
            </Text>
          </SettingsHeader>
          <Grid.Col span={12}>
            <Text size="sm" strong={true} display="technical" tt="uppercase">
              {t("settings:economySettings.invoice")}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="invoiceemail"
              render={({ field, fieldState }) => {
                return (
                  <TextInput
                    {...field}
                    id="invoiceemail"
                    data-testid="invoiceemail-input"
                    required={true}
                    label={t("common:invoiceemail")}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    disabled={!isAdmin}
                  />
                );
              }}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Space height="25px" />
            <Controller
              control={control}
              name="alwaysInvoice"
              render={({ field, fieldState }) => {
                return (
                  <Checkbox
                    checked={field.value}
                    id="alwaysInvoice"
                    label={t("settings:economySettings.preferInvoice")}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    disabled={!isAdmin}
                  />
                );
              }}
            />
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
      <SettingsFormCTAs>
        <CTA
          disabled={!isAdmin || !isValid}
          id="save"
          data-testid="submit-btn"
          size="lg"
          type="submit"
          loading={isPending}
        >
          {t("actions:save")}
        </CTA>
      </SettingsFormCTAs>
    </form>
  );
};

interface IndexRegulationFormProps {
  companyId: number;
  isAdmin: boolean;
}

const IndexRegulationForm = ({ companyId, isAdmin }: IndexRegulationFormProps) => {
  const queryClient = useQueryClient();
  const ref = useRef<HTMLFormElement>(null);

  const { data } = useGetCompanyIndexRegulationPercentage(companyId, { enabled: !!companyId });

  const { handleSubmit, control } = useForm<IndexRegulationFormInput>({
    resolver: zodResolver(IndexRegulationValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: { percentage: data?.value ?? 0 },
  });

  const { mutate: updateIndexRegulation, isPending } = useMutation({
    mutationFn: async (formData: IndexRegulationFormInput) => {
      return await putUpdateCompanyIndexRegulationPercentage(companyId, formData.percentage);
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.updateSuccess"),
          t("notifications:settings.updateIndexRegulation"),
          "index-regulation-updated-success"
        )
      );
      queryClient.invalidateQueries({ queryKey: ["getIndexRegulationPercentage", companyId] });
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.updateError"),
          t("notifications:settings.updateIndexRegulationError"),
          "index-regulation-updated-error"
        )
      );
    },
  });

  const onSubmit = (formData: IndexRegulationFormInput) => {
    updateIndexRegulation(formData);
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:economySettings.indexRegulation")}
            </Text>
          </SettingsHeader>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="percentage"
              render={({ field, fieldState }) => (
                <NumberInput
                  {...field}
                  id="indexRegulationPercentage"
                  label={t("contracts:yearlyIncreaseIndexRegulation")}
                  suffix="%"
                  min={0}
                  max={100}
                  decimalScale={2}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  disabled={!isAdmin}
                />
              )}
            />
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
      <SettingsFormCTAs>
        <CTA disabled={!isAdmin} id="save-index-regulation" size="lg" type="submit" loading={isPending}>
          {t("actions:save")}
        </CTA>
      </SettingsFormCTAs>
    </form>
  );
};
